// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

import { TextField } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

type Props = {
  rootStyle?: {},
};

export function FieldMexicanStreetNumber({ rootStyle }: Props): Node {
  const inputId = useUniqueId();
  const [value, handleChange] = useUserInputAsTextField('mexicanStreetNumber');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('mexicanStreetNumber');
  const submitting = useSubmitting();

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('street_number');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div style={rootStyle} data-test-name="address-street-number">
      <label htmlFor={inputId}>Número</label>

      <TextField
        id={inputId}
        type="text"
        autoComplete="off"
        value={value}
        onChange={handleChange}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="address-street-number"
      />

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          No dejes de informar el número de domicilio.
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          El número de domicilio que informaste no es válido. :/
        </ErrorLabel>
      );
  }
}
